import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "enterprise-mcp-media";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title:
						"Enterprise MCP: Governance, Billing, and Access Control - AgentFramer",
				},
				{
					name: "description",
					content:
						"What enterprise teams need before they hand an agent the keys to media generation. Workspace controls, budget caps, audit trails.",
				},
				{
					property: "og:title",
					content: "Enterprise MCP: Governance, Billing, and Access Control",
				},
				{
					property: "og:description",
					content:
						"How enterprises run AI media generation through MCP safely.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					Enterprise MCP: governance, billing, and access control
				</h1>
				<p className="text-sm text-muted-foreground">
					Handing agents the keys to a media generation budget is a real spend
					decision, not a feature toggle. Here is what enterprise teams put in
					place before they do it.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					The $4,000 surprise invoice
				</h2>
				<p>
					A growth team at a mid-size SaaS company wired an internal Slack bot
					to an MCP media server in late January. It worked beautifully. By the
					end of February, finance flagged a $4,000 line item nobody recognized.
					The agent had been retrying failed video generations on a loop, each
					one cheap on its own, none of them visible to anyone who could have
					stopped it. There was no audit trail tied to a user, no spend ceiling,
					and no alert. The team learned about it from accounting.
				</p>
				<p>
					This is the failure mode enterprise governance is built to prevent.
					Not malicious actors, not exotic attacks, just an agent doing exactly
					what it was told without anyone watching the meter. The fix is not a
					policy document. It is four concrete controls: who can call what, how
					much can be spent, what gets stored where for how long, and who
					answers when something goes wrong.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Workspace per product team, not per company
				</h2>
				<p>
					AgentFramer organizes everything inside a workspace. A workspace owns
					a credit balance, a member list, an MCP URL, a storage scope, and a
					region pin. The single most common mistake we see is enterprises
					modeling AgentFramer like Slack: one workspace for the whole company,
					everyone added in. That puts the marketing team's experiments and the
					engineering team's production traffic on the same balance and the same
					MCP URL.
				</p>
				<p>
					The pattern that works is one workspace per product team. The growth
					team has its own workspace, its own balance, its own MCP URL. The
					content ops team has another. A leaked URL has a blast radius limited
					to one team's cap. A runaway agent stops at one team's hard cap, not
					at the company aggregate. The{" "}
					<Link
						to="/help/quick-start"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						quick-start guide
					</Link>{" "}
					walks through the first workspace; create the rest from the dashboard
					as teams onboard.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Three roles plus a service identity
				</h2>
				<p>Each workspace ships with three roles:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">Admin</strong>: invites and
						removes members, manages billing, rotates the MCP URL, configures
						budget caps and webhooks.
					</li>
					<li>
						<strong className="text-foreground">Developer</strong>: configures
						agents, generates API keys, reads billing and usage, but cannot
						change billing or rotate URLs.
					</li>
					<li>
						<strong className="text-foreground">Viewer</strong>: read-only
						access to generations, audit log, and usage dashboards. Finance and
						compliance reviewers belong here.
					</li>
				</ul>
				<p>
					An agent connecting via MCP uses a service identity, distinct from any
					human user. Treat it like an AWS IAM service role: rotate its token on
					a fixed cadence (90 days is the standard for service identities),
					scope it to one workspace, and never use a human's token in
					production. The sample policy below is what we recommend dropping into
					your IAM reference docs:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`{
  "identity": "agent.growth-bot",
  "workspace_id": "ws_4kP9x2",
  "role": "developer",
  "permissions": [
    "generate_image",
    "generate_video",
    "get_credits",
    "list_recent_generations"
  ],
  "denied": [
    "invite_team_member",
    "top_up_balance",
    "delete_workspace"
  ],
  "token_rotation_days": 90,
  "ip_allowlist": ["10.0.0.0/8"],
  "created_by": "user_admin_192",
  "created_at": "2026-04-12T09:14:00Z"
}`}</pre>

				<h2 className="text-base font-medium text-foreground pt-2">
					Budget caps: hard floor, soft alert
				</h2>
				<p>
					Spend protection has two layers. The hard cap is the credit balance
					itself: when it hits zero, generations return an error and the agent
					stops. There is no overage. The soft alert fires at a configurable
					threshold (default 75% of the workspace's typical monthly run) and
					emails admins, optionally posting to a Slack webhook.
				</p>
				<p>
					On top of that, agents can self-throttle. The <code>get_credits</code>{" "}
					tool is callable from the same MCP server the agent is already
					connected to. The pattern we recommend in our{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool call patterns guide
					</Link>{" "}
					is a pre-flight check on any run that could exceed five dollars in a
					single tool call: video generation, batch image runs, anything the
					agent itself estimates as expensive. If the balance is below a
					configured floor, the agent stops, posts to its handler, and waits for
					a top-up rather than chewing through the remainder.
				</p>
				<p>
					Hard cap is the seatbelt. Soft alert is the dashboard light.
					Pre-flight
					<code> get_credits</code> is the driver checking the gauge before
					merging onto the highway. All three together are how a $4,000 surprise
					becomes a $40 stop.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Audit log: who, when, what, how much
				</h2>
				<p>
					Every successful tool call writes one row to the audit log. The shape
					is stable, queryable from the dashboard, and exportable as JSON for
					your data warehouse:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`{
  "ts": "2026-04-29T14:22:08.413Z",
  "request_id": "req_01HW8K2N4Q",
  "workspace_id": "ws_4kP9x2",
  "user_id": "agent.growth-bot",
  "tool": "generate_image",
  "model": "flux-pro-1.1",
  "prompt_hash": "sha256:9f1c...e802",
  "cost_usd": 0.042,
  "result_url": "https://cdn.agentframer.com/ws_4kP9x2/g_8K2N4Q.png",
  "region": "eu-west-1",
  "retention_days": 30
}`}</pre>
				<p>
					Notice <code>prompt_hash</code> rather than the raw prompt. Full
					prompts are stored separately under the workspace's retention policy,
					so the audit log itself can be retained longer (default seven years
					for enterprise plans) without copying potentially sensitive text into
					a long-lived index. Compliance gets the trail, the prompt store
					follows your data policy.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					SIEM webhook to Splunk or Datadog
				</h2>
				<p>
					Audit log entries also fan out via webhook in real time. Point the
					webhook at your SIEM's HTTP event collector and every generation lands
					in your existing dashboards alongside the rest of your logs:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`POST https://http-intake.logs.datadoghq.eu/api/v2/logs
X-AgentFramer-Signature: t=1714402928,v1=4f3a...
Content-Type: application/json

{
  "event": "generation.completed",
  "version": "1",
  "ts": "2026-04-29T14:22:08.413Z",
  "request_id": "req_01HW8K2N4Q",
  "workspace_id": "ws_4kP9x2",
  "user_id": "agent.growth-bot",
  "tool": "generate_image",
  "model": "flux-pro-1.1",
  "cost_usd": 0.042,
  "balance_after_usd": 312.18,
  "region": "eu-west-1",
  "result_url": "https://cdn.agentframer.com/ws_4kP9x2/g_8K2N4Q.png"
}`}</pre>
				<p>
					Signatures use HMAC-SHA256 with a workspace secret. The same payload
					format works for Splunk HEC and any custom collector; we publish the
					full schema in the{" "}
					<Link
						to="/help/mcp"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP reference
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Storage, retention, and data residency
				</h2>
				<p>
					Generated media is stored at signed URLs scoped to the workspace.
					Default retention is 30 days. Enterprise plans can shorten that to one
					day for sensitive workloads, or extend it for archival. If your policy
					is "no model output lives on a vendor longer than necessary", set
					retention to one day and have the agent download anything it needs to
					keep into your own object store.
				</p>
				<p>
					Data residency is pinned per workspace. Pick EU or US at workspace
					creation; the MCP endpoint, the storage bucket, and the audit log all
					stay inside that region. Cross-region replication is opt-in. The model
					providers themselves vary; the model library marks each model's
					residency, and several models offer EU-only inference for GDPR-bound
					workloads.
				</p>
				<p>
					Prompts and outputs are subject to GDPR data subject requests via the
					dashboard's deletion API. Output ownership sits with the workspace
					under the AgentFramer terms; underlying model licenses are linked from
					each model page. SOC 2 Type II report is available under NDA from any
					Admin in the security panel.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When enterprise governance is too much
				</h2>
				<p>
					Most of this is overhead for most teams. If you are a three-person
					startup prototyping a feature, a solo developer building a side
					project, or a single-product company with one shared agent and one
					shared budget, you do not need workspace-per-team, you do not need a
					90-day token rotation cadence, and you do not need a SIEM webhook.
				</p>
				<p>
					For those teams the right setup is the default: one workspace, one
					admin, a credit balance you check weekly, and the dashboard's built-in
					audit view. Hard cap at whatever monthly number you can absorb without
					calling a board meeting. The whole governance stack above is a tax
					that only pays for itself when there are real cost centers, real
					compliance reviewers, and real human users on different teams who
					should not see each other's prompts. Until then, ignore it. Build the
					product.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					The starting checklist
				</h2>
				<p>
					When the team passes the threshold where governance starts to matter,
					the rollout that works:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>One workspace per product team. Region pinned at creation.</li>
					<li>
						Hard cap set to the team's expected monthly spend. Soft alert at
						75%.
					</li>
					<li>
						Service identity per agent, scoped to one workspace, token rotated
						every 90 days.
					</li>
					<li>
						Pre-flight <code>get_credits</code> on any tool call estimated above
						$5.
					</li>
					<li>
						Audit webhook pointed at Splunk or Datadog. Viewer role added for
						finance and compliance.
					</li>
					<li>
						Retention set to match your data policy. Default 30 days is a
						reasonable starting point.
					</li>
				</ul>
				<p>
					Six bullets. Maybe an afternoon of work. The difference between an
					agent your finance team trusts and the one that ships you a $4,000
					invoice. None of this gets in the way of an agent doing useful work,
					and all of it is reversible. Start with one workspace and the hard
					cap, layer the rest in as the surface area grows.
				</p>
			</div>
		</article>
	);
}
